/**
 * Program Home "Mine" grouping, the Workroom signals panel, and (SAD-143) the
 * global dashboard's tenant-wide feed — the caller's own assigned actions
 * (SAD-170 / D15), scoped to one program when `programId` is given or
 * tenant-wide when it's omitted. ListMyAssignedActions resolves the
 * recipient server-side (no user/contact id on the request) and also
 * computes each row's exact destination route, so this hook is a thin fetch.
 *
 * D113: its PROGRAM_TASK rows are the old deliverable model and are dropped
 * here, so no consumer can show them. The caller's deliverable work comes
 * from the contract model instead — `useMyDeliverableActions` below.
 */ import { DeliveryTypes } from '@mono/proto-clients';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient } from '../../../lib/connect/clients';
import { useCmAccess } from '../../../pages/Delivery/Deliverables/access';
import { OccurrenceWindow, useDuties, useOccurrenceDecisions, useOccurrences } from '../../../pages/Delivery/Deliverables/hooks';
import { OCCURRENCE_STATES, occurrenceStatus } from '../../../pages/Delivery/Deliverables/lib/status';
export const MY_ASSIGNED_ACTIONS_KEY = 'my assigned actions';
// Fetched above the 5-row display cap so DoNextQueue can detect "more than
// 5" locally instead of relying solely on the server's hasMore flag.
const FETCH_LIMIT = 20;
function withoutProgramTasks(resp) {
    return new DeliveryTypes.ListMyAssignedActionsResponse({
        ...resp,
        actions: resp.actions.filter((a)=>a.kind !== DeliveryTypes.AssignedActionKind.PROGRAM_TASK)
    });
}
export function useMyAssignedActions(programId) {
    return useQuery({
        // Normalize undefined/null to the same key so a program-scoped caller and
        // the tenant-wide dashboard caller (which omits programId) never diverge
        // on how "no program" is represented in the cache.
        queryKey: [
            MY_ASSIGNED_ACTIONS_KEY,
            programId ?? null
        ],
        queryFn: ()=>deliveryClient.listMyAssignedActions({
                programId: programId ?? '',
                limit: FETCH_LIMIT
            }),
        select: withoutProgramTasks,
        staleTime: 30000
    });
}
const DECISION_LABEL = {
    waiver: 'Waiver awaiting your approval',
    not_owed: 'Not-owed mark awaiting your approval'
};
/**
 * The caller's deliverable work on one program (D113), in the order it
 * should be done:
 *
 *   - proposals awaiting approval that they may decide — any holder of
 *     `area:execution` may approve (D112 #2), but never their own proposal;
 *   - the dates they may act on: late and due in 14 days for whoever does the
 *     work (`area:execution`), awaiting review for whoever records the
 *     verdict (`area:execution.verdict`).
 *
 * The contract model has no per-person assignee on a date, so "yours" is the
 * work your grants let you do. Program-scoped only: with no programId there
 * is nothing to read (a tenant-wide read would need a permission resolve per
 * program).
 */ export function useMyDeliverableActions(programId, { enabled = true } = {}) {
    const pid = enabled ? programId ?? '' : '';
    const access = useCmAccess(pid);
    const userId = useAuth().user?.id ?? '';
    const states = [
        ...access.canWrite ? [
            'late'
        ] : [],
        ...access.canVerdict ? [
            'awaiting_review'
        ] : [],
        ...access.canWrite ? [
            'due_soon'
        ] : []
    ];
    const occ = useOccurrences(pid, {
        window: OccurrenceWindow.ALL,
        states
    }, {
        enabled: states.length > 0
    });
    const decisions = useOccurrenceDecisions(pid, {
        openOnly: true
    }, {
        enabled: access.canWrite && !!userId
    });
    const duties = useDuties(pid, {
        includeRules: true
    });
    const dutyById = new Map((duties.data?.duties ?? []).map((d)=>[
            d.id,
            d
        ]));
    const base = `/delivery/programs/${pid}`;
    const titleOf = (dutyId, label)=>[
            dutyById.get(dutyId)?.title || 'Untitled deliverable',
            label
        ].filter(Boolean).join(' · ');
    const rows = [];
    const decisionOcc = new Map((decisions.data?.occurrences ?? []).map((o)=>[
            o.id,
            o
        ]));
    for (const d of decisions.data?.decisions ?? []){
        if (!userId || d.proposedByUserId === userId) continue;
        const o = decisionOcc.get(d.occurrenceId);
        rows.push({
            key: `approval:${d.changeSetId}`,
            kind: 'approval',
            title: o ? titleOf(o.dutyId, o.label) : 'Deliverable date',
            state: DECISION_LABEL[d.kind] ?? 'Awaiting your approval',
            tone: 'info',
            dueOn: o?.dueOn ?? '',
            href: `${base}/deliverables?occ=${d.occurrenceId}`
        });
    }
    const rank = (s)=>OCCURRENCE_STATES.indexOf(s);
    const dates = [
        ...occ.data?.occurrences ?? []
    ].sort((a, b)=>rank(a.status?.state ?? '') - rank(b.status?.state ?? '') || (a.dueOn || '9999').localeCompare(b.dueOn || '9999') || a.id.localeCompare(b.id));
    for (const o of dates){
        const status = occurrenceStatus(o, dutyById.get(o.dutyId)?.finalReviewTier);
        rows.push({
            key: `date:${o.id}`,
            kind: 'date',
            title: titleOf(o.dutyId, o.label),
            state: status.label,
            tone: status.tone,
            dueOn: o.dueOn,
            href: `${base}/deliverables?occ=${o.id}`
        });
    }
    return {
        rows,
        isLoading: access.isLoading || occ.isLoading || decisions.isLoading,
        isError: occ.isError || decisions.isError
    };
}
